import { RefObject } from 'react';
import { useForm } from 'react-hook-form';
import TextField from '@shared/components/TextField';
import { useCreateNewOrUpdateServiceMutation } from '../queries';
import { ServiceCreateOrUpdateData } from '../entity';
import AddWorkPauseBlock from './AddWorkPauseBlock';
import { SubmitFormDialog } from '@shared/components/dialogs/submit-form-dialog';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useSubscriptionPlan } from '@shared/store';
import { SubscriptionPlansEnum } from '@features/subscription/entity';

type ServiceFormDialogProps = {
  defaultValues?: Partial<ServiceCreateOrUpdateData>;
  dialogRef: RefObject<ImperativeFormDialogRefType | null>;
};

const ServiceFormDialog = (props: ServiceFormDialogProps) => {
  const { defaultValues, dialogRef } = props;
  const { t } = useIntlTranslations();
  const methods = useForm<ServiceCreateOrUpdateData>({ defaultValues });
  const { mutate: createServicemMutation } = useCreateNewOrUpdateServiceMutation({
    onSuccess: () => {
      methods.reset();
    },
  });
  const isExpert = useSubscriptionPlan() === SubscriptionPlansEnum.EXPERT;

  return (
    <SubmitFormDialog
      methods={methods}
      ref={dialogRef}
      onOpen={() => methods.reset(defaultValues)}
      onSubmit={data => createServicemMutation(data)}
      title={t('addServiceButton.addService')}
      dialogHelperText={t('addServiceButton.helperText')}
    >
      <>
        <TextField
          id="service-name"
          control={methods.control}
          fieldPath="serviceName"
          label={t('addServiceItemButton.serviceName')}
          type="text"
          fullWidth
          required
        />
        <TextField
          id="service-price"
          control={methods.control}
          fieldPath="basePrice"
          label={t('addServiceItemButton.pricePerService')}
          type="number"
          fullWidth
          required
        />
        <AddWorkPauseBlock
          id="service-work-block"
          control={methods.control}
          fieldPath="blocks"
          defaultValues={defaultValues?.blocks || []}
        />
        {isExpert && (
          <>
            <TextField
              id="service-public-name"
              control={methods.control}
              fieldPath="publicName"
              label={t('addServiceItemButton.publicName')}
              type="text"
              fullWidth
            />
            <TextField
              id="service-public-description"
              control={methods.control}
              fieldPath="publicDescription"
              label={t('addServiceItemButton.publicDescription')}
              type="text"
              fullWidth
              multiline
              rows={3}
            />
          </>
        )}
      </>
    </SubmitFormDialog>
  );
};

export default ServiceFormDialog;
